import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { create } from 'zustand'

import { paths } from 'src/config/paths'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import TabButton from 'src/shared/components/ui/navigation/tab-button'
import type { IconName } from 'src/shared/components/ui/navigation/tab-button-icon'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { LogoutIcon } from 'src/shared/features/users/components/marketplace/icons/logout'
import ProfileAvatar from 'src/shared/features/users/components/profile-photos/profile-avatar'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useContainerScrollBreakpoint } from 'src/shared/hooks/use-container-scroll-breakpoint'
import { useHorizontalScroll } from 'src/shared/hooks/use-horizontal-scroll'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesDefault } from 'src/shared/utils/tailwind-styles'

interface AppWindowFrameProps {
  children: React.ReactNode
}

export interface TabButtonConfig {
  iconName: IconName
  activeIconName?: IconName
  name: string
  path: string
}
interface TabletPortraitViewProps {
  tabButtons: TabButtonConfig[]
  logoLink: string
  menuComponent?: React.ReactNode
  menuOnClick?: () => void
}

interface TabletPortraitViewProps {
  tabButtons: TabButtonConfig[]
  logoLink: string
  menuComponent?: React.ReactNode
  menuOnClick?: () => void
}

function TabletPortraitView({ tabButtons, logoLink, menuComponent, menuOnClick }: TabletPortraitViewProps) {
  const { bpSwitch } = useBreakpoint()
  const { data: currentUser } = useMyUserData()

  const location = useLocation()

  const mobileTabBarRef = useHorizontalScroll<HTMLDivElement>(250)

  const showRightBorder = !useContainerScrollBreakpoint({
    containerElement: mobileTabBarRef,
    breakpoint: 2,
    reverse: true,
    vertical: false,
  })

  const showLeftBorder = useContainerScrollBreakpoint({
    containerElement: mobileTabBarRef,
    breakpoint: 2,
    reverse: false,
    vertical: false,
  })

  return (
    <div
      className={cn(
        'flex h-[70px] w-full flex-row items-stretch justify-between transition-all duration-200 ease-out md:w-[70px] xl:w-[80px]',
      )}
    >
      <div className="flex min-w-[200px] shrink flex-row items-center">
        <Link reloadDocument={true} to={logoLink} className="ml-[15px] flex shrink-0 items-center justify-center">
          <Iconmark className="size-[28px] text-white md:size-[30px] xl:size-[35px]" />
        </Link>
        <div className="mx-[15px] min-h-[20px] w-px self-center bg-white/25" />
        <div className="flex min-h-[60px] min-w-[100px] flex-1 flex-row items-center">
          <div
            className={cn(
              'z-10 h-[50px] w-[1px] bg-primary opacity-0 shadow-[5px_0px_12px_3px_rgba(27,48,41,1)] transition-all duration-200',
              showLeftBorder && 'opacity-100',
            )}
          />
          <div
            ref={mobileTabBarRef}
            className="flex w-full flex-row items-center overflow-x-auto overflow-y-hidden scrollbar-none"
          >
            <div className="flex w-full flex-row gap-1 md:gap-[19px] xl:gap-6">
              {tabButtons.map((tabButton) => (
                <TabButton
                  key={tabButton.name}
                  label={tabButton.name}
                  iconName={tabButton.iconName}
                  activeIconName={tabButton.activeIconName}
                  to={tabButton.path}
                  isActive={location.pathname.startsWith(tabButton.path)}
                  hideLabel={false}
                  iconClassName="scale-[0.75] md:scale-[0.92] xl:scale-100"
                  labelClassName="text-[12px] md:text-[13px] xl:text-[14px]"
                  className="flex-row py-0"
                  shouldLabelBeInsideHoverContainer={true}
                  hoverContainerClassName="h-auto w-auto py-[10px] pl-[10px] pr-3 group-hover:scale-100 gap-2"
                />
              ))}
            </div>
          </div>
          <div
            className={cn(
              'z-10 h-[50px] w-[1px] bg-primary opacity-0 shadow-[-5px_0px_12px_3px_rgba(27,48,41,1)] transition-all duration-200',
              showRightBorder && 'opacity-100',
            )}
          />
          {/* {hasScroll && <div className="w-[1px] min-h-[40px] self-center bg-white/25" />} */}
        </div>
        {/* <div className="ml-3 flex shrink-0 items-center justify-center">
          <div className="flex size-[40px] items-center justify-center rounded-full bg-white transition-all duration-200 hover:scale-110 hover:cursor-pointer md:size-[50px] xl:size-[55px]">
            <PlusIcon className="size-[12px] text-primary md:size-[15px] xl:size-[16.5px]" />
          </div>
        </div> */}
      </div>
      <div className="my-auto ml-3 flex shrink-0 flex-row gap-3 hover:cursor-pointer" onClick={menuOnClick}>
        {menuComponent !== undefined ? (
          menuComponent
        ) : (
          <ProfileAvatar
            size={bpSwitch({
              xl: 50,
              md: 42,
              default: 37,
            })}
            userId={currentUser?.id ?? -1}
            className="box-content shrink-0 border-[3px] border-white"
          />
        )}
      </div>
    </div>
  )
}

interface AppWindowInsetsContextProps {
  insets: {
    top: number
    right: number
    bottom: number
    left: number
  }
  setInsets: (insets: { top: number; right: number; bottom: number; left: number }) => void
}

/** Used to get the insets/padding around the content of the page. */
export const useAppWindowInsetsStore = create<AppWindowInsetsContextProps>()((set) => ({
  insets: { top: 0, right: 0, bottom: 0, left: 0 },
  setInsets: (insets: { top: number; right: number; bottom: number; left: number }) => set({ insets }),
}))

interface AppWindowFrameProps {
  children: React.ReactNode
  logoLink?: string
  tabButtons: TabButtonConfig[]
  activeTabIdx: number
  menuComponent?: React.ReactNode
  menuLabel?: string
  menuOnClick?: () => void
  showLogoutButton?: boolean
  logoutButtonOnClick?: () => void
}

export default function AppWindowFrame({
  children,
  logoLink = paths.home.path,
  tabButtons,
  activeTabIdx,
  menuComponent,
  menuOnClick,
  menuLabel = 'Menu',
  showLogoutButton = false,
  logoutButtonOnClick,
}: AppWindowFrameProps) {
  const { setInsets } = useAppWindowInsetsStore()
  const { bpSwitch, isSmTabletOrSmaller, breakpoint } = useBreakpoint()
  const { data: currentUser } = useMyUserData()

  useEffect(() => {
    document.body.style.setProperty('background-color', '#1B3029')
    return () => {
      document.body.style.removeProperty('background-color')
    }
  }, [])

  /** The conatiner that surrounds the content of the page. Does not include the sidebar. */
  const contentRef = useRef<HTMLDivElement>(null)
  const tabBarRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const [isSidebarHovered, setIsSidebarHovered] = useState(false)

  // Shows the bottom border on the tab bar when content is hidden under the tab bar.
  const showBottomBorder = !useContainerScrollBreakpoint({
    containerElement: tabBarRef,
    breakpoint: 2,
    reverse: true,
  })

  const showTopBorder = useContainerScrollBreakpoint({
    containerElement: tabBarRef,
    breakpoint: 2,
    reverse: false,
  })

  // if the sidebar is hovered, we expand the sidebar
  useEffect(() => {
    const sidebar = sidebarRef.current
    if (!sidebar) {
      return
    }
    const handleMouseEnter = () => {
      setIsSidebarHovered(true)
    }
    const handleMouseLeave = () => {
      setIsSidebarHovered(false)
    }
    sidebar.addEventListener('mouseenter', handleMouseEnter)
    sidebar.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      sidebar.removeEventListener('mouseenter', handleMouseEnter)
      sidebar.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isSmTabletOrSmaller])

  useEffect(() => {
    if (contentRef.current) {
      const x = contentRef.current.getBoundingClientRect().x
      const y = contentRef.current.getBoundingClientRect().y
      const width = contentRef.current.getBoundingClientRect().width
      const height = contentRef.current.getBoundingClientRect().height

      setInsets({
        top: Math.ceil(y),
        left: Math.ceil(x),
        bottom: Math.ceil(window.innerHeight - height - y),
        right: Math.ceil(window.innerWidth - width - x),
      })
    }
  }, [isSidebarHovered, isSmTabletOrSmaller, breakpoint, setInsets])

  useEffect(() => {
    if (!contentRef.current) {
      return
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const x = entry.target.getBoundingClientRect().x
        const y = entry.target.getBoundingClientRect().y
        const width = entry.target.getBoundingClientRect().width
        const height = entry.target.getBoundingClientRect().height

        setInsets({
          top: Math.ceil(y),
          left: Math.ceil(x),
          bottom: Math.ceil(window.innerHeight - height - y),
          right: Math.ceil(window.innerWidth - width - x),
        })
      }
    })
    observer.observe(contentRef.current)
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [contentRef, setInsets])

  return (
    <main
      className={cn(
        'flex h-dvh w-full flex-row overflow-hidden bg-primary py-[20px] pr-[20px] xl:py-[29px] xl:pr-[26px]',
        isSmTabletOrSmaller && 'flex-col pl-[20px] pt-[0px]',
      )}
    >
      {isSmTabletOrSmaller ? (
        <TabletPortraitView
          menuOnClick={menuOnClick}
          tabButtons={tabButtons}
          logoLink={logoLink}
          menuComponent={menuComponent}
        />
      ) : (
        <div
          ref={sidebarRef}
          className={cn(
            'flex h-full flex-col items-stretch justify-between pb-[15px] transition-all duration-200 ease-out md:w-[70px] xl:w-[80px]',
            isSidebarHovered && 'px-2 md:w-[120px] xl:w-[140px]',
          )}
        >
          <div className="flex min-h-[240px] shrink flex-col items-center">
            <Link
              reloadDocument={true}
              to={logoLink}
              className="mb-6 flex w-full shrink-0 items-center justify-center py-[15px]"
            >
              <Iconmark className="size-[28px] text-white md:size-[30px] xl:size-[35px]" />
            </Link>
            <div className="flex min-h-[100px] w-full flex-1 flex-col items-center">
              {showTopBorder && <div className="z-10 h-px w-[70%] shadow-[0_5px_12px_3px_rgba(27,48,41,1)]" />}
              <div ref={tabBarRef} className="flex w-full flex-col items-center overflow-y-auto py-1 scrollbar-none">
                <div className="flex w-full flex-col gap-[18px] md:gap-[19px] xl:gap-6">
                  {tabButtons.map((tabButton, idx) => (
                    <TabButton
                      key={tabButton.name}
                      label={tabButton.name}
                      iconName={tabButton.iconName}
                      activeIconName={tabButton.activeIconName}
                      to={tabButton.path}
                      isActive={idx === activeTabIdx}
                      hideLabel={!isSidebarHovered}
                      iconClassName="scale-[0.75] md:scale-[0.92] xl:scale-100"
                      labelClassName="text-[12px] md:text-[13px] xl:text-[14px]"
                    />
                  ))}
                </div>
              </div>
              {showBottomBorder && <div className="z-10 h-px w-[70%] shadow-[0_-5px_12px_3px_rgba(27,48,41,1)]" />}
            </div>
            {/* <div className="mt-6 flex shrink-0 items-center justify-center">
              <div className="flex size-[40px] items-center justify-center rounded-full bg-white transition-all duration-200 hover:scale-110 hover:cursor-pointer md:size-[50px] xl:size-[55px]">
                <PlusIcon className="size-[12px] text-primary md:size-[15px] xl:size-[16.5px]" />
              </div>
            </div> */}
          </div>
          <div>
            {showLogoutButton && (
              <div
                className="mt-10 flex shrink-0 flex-col items-center gap-3 hover:cursor-pointer"
                onClick={logoutButtonOnClick}
              >
                <div className="box-content flex size-[50px] shrink-0 items-center justify-center rounded-full border-[3px] border-white bg-muted">
                  <LogoutIcon className="size-6" color="#D8D8D6" />
                </div>
                <span
                  className={cn(
                    'text-sm font-semibold text-white transition-all duration-300',
                    !isSidebarHovered && 'opacity-0',
                  )}
                >
                  Log out
                </span>
              </div>
            )}
            <div className="mt-10 flex shrink-0 flex-col items-center gap-3 hover:cursor-pointer" onClick={menuOnClick}>
              {menuComponent !== undefined ? (
                menuComponent
              ) : (
                <ProfileAvatar
                  size={bpSwitch({
                    xl: 50,
                    md: 42,
                    default: 37,
                  })}
                  userId={currentUser?.id ?? -1}
                  className="box-content shrink-0 border-[3px] border-white"
                />
              )}
              <div
                className={cn(
                  'text-sm font-semibold text-white transition-all duration-300',
                  !isSidebarHovered && 'opacity-0',
                )}
              >
                {menuLabel}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="w-full flex-1 overflow-hidden rounded-[25px] bg-white" ref={contentRef}>
        <div className={cn('size-full flex-1 overflow-y-auto', ScrollBarTailwindClassesDefault)}>{children}</div>
      </div>
    </main>
  )
}
